import api from './client'
import type { Issue, ActivityLog, WorkType, TimeLogStatus } from '../types'

export interface IssueFilters {
  status?: string
  priority?: string
  assignee?: string
  label?: string
  milestone?: string
  component?: string
  search?: string
  workType?: WorkType
  createdFrom?: string
  createdTo?: string
  dueFrom?: string
  dueTo?: string
}

export type IssueMutationPayload = Partial<Issue> & {
  assigneeIds?: string[]
  assigneeEstimates?: { userId: string; estimatedHours?: number | null; workType?: WorkType | null; dueDate?: string | null }[]
  workEstimates?: { workType: WorkType; hours: number }[]
  labelIds?: string[]
}

export type ReadyForNextStagePayload = {
  targetStatusId: string
  labelIds?: string[]
  targetIndex?: number
  internalNote?: {
    content: string
  }
  timeLog?: {
    workType: WorkType
    status: TimeLogStatus
    hours: number
    progressPercent?: number
    progressNotes?: string
    completedDetails?: string
    loggedDate: string
  }
}

export const issuesApi = {
  listIssues: (projectId: string, params?: IssueFilters) =>
    api.get<{ data: Issue[] }>(`/projects/${projectId}/issues`, { params }),

  createIssue: (projectId: string, data: IssueMutationPayload) =>
    api.post<{ data: Issue }>(`/projects/${projectId}/issues`, data),

  reorderIssue: (projectId: string, data: { issueId: string; targetStatusId: string; targetIndex: number }) =>
    api.put<{ data: { success: boolean } }>(`/projects/${projectId}/reorder`, data),

  getIssue: (id: string) => api.get<{ data: Issue }>(`/issues/${id}`),

  updateIssue: (id: string, data: IssueMutationPayload) =>
    api.put<{ data: Issue }>(`/issues/${id}`, data),

  readyForNextStage: (id: string, data: ReadyForNextStagePayload) =>
    api.post<{ data: Issue }>(`/issues/${id}/ready-for-next-stage`, data),

  deleteIssue: (id: string) => api.delete(`/issues/${id}`),

  // Sub-issues
  listSubIssues: (id: string) => api.get<{ data: Issue[] }>(`/issues/${id}/sub-issues`),

  createSubIssue: (id: string, data: IssueMutationPayload) =>
    api.post<{ data: Issue }>(`/issues/${id}/sub-issues`, data),

  // Relations
  addRelation: (id: string, data: { toIssueId: string; relationType: string }) =>
    api.post(`/issues/${id}/relations`, data),

  removeRelation: (id: string, relationId: string) =>
    api.delete(`/issues/${id}/relations/${relationId}`),

  // Activity
  listActivity: (id: string) => api.get<{ data: ActivityLog[] }>(`/issues/${id}/activity`),

  // My issues
  listMyIssues: (params?: IssueFilters) =>
    api.get<{ data: Issue[] }>('/me/issues', { params }),
}
